import { useEffect, useState, useCallback, useMemo } from "react";
import api from "../api/axios";
import { formatInr } from "../utils/formatInr";
import "./ItemMaster.css";

export default function ClientInventoryTab({ clientId, projectId }) {
  const [loading, setLoading] = useState(true);
  const [allocations, setAllocations] = useState([]);
  const [summary, setSummary] = useState({ totalAllottedAmount: 0, netQuantity: 0, totalEntries: 0 });
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  // Modals state
  const [showAllotModal, setShowAllotModal] = useState(false);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [selectedTx, setSelectedTx] = useState(null);

  // Inventory lookup for picking items
  const [inventoryList, setInventoryList] = useState([]);
  const [loadingInv, setLoadingInv] = useState(false);

  // Multi-item Allotment state
  const [allotItemsList, setAllotItemsList] = useState([]);
  const [pickerItemId, setPickerItemId] = useState("");
  const [pickerSearch, setPickerSearch] = useState("");
  const [pickerQty, setPickerQty] = useState("1");
  const [pickerRate, setPickerRate] = useState("");
  const [pickerNotes, setPickerNotes] = useState("");
  const [pickerError, setPickerError] = useState("");
  const [allotStep, setAllotStep] = useState("form"); // "form" | "further-steps"
  const [allotSuccessData, setAllotSuccessData] = useState(null);

  const [clientOrders, setClientOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState("new");
  const [allotNotes, setAllotNotes] = useState("");
  const [submittingAllot, setSubmittingAllot] = useState(false);
  const [allotError, setAllotError] = useState("");

  // Return form state
  const [returnQty, setReturnQty] = useState("");
  const [returnNotes, setReturnNotes] = useState("");
  const [submittingReturn, setSubmittingReturn] = useState(false);
  const [returnError, setReturnError] = useState("");

  // Fetch client allocations
  const loadAllocations = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    setError("");
    try {
      const params = {};
      if (projectId) params.projectId = projectId;
      const res = await api.get(`/inventory/client-allocations/${clientId}`, { params });
      setAllocations(res.data?.data?.transactions || []);
      setSummary(res.data?.data?.summary || { totalAllottedAmount: 0, netQuantity: 0, totalEntries: 0 });
    } catch (err) {
      console.error(err);
      setError("Failed to load inventory allocations for this client.");
    } finally {
      setLoading(false);
    }
  }, [clientId, projectId]);

  useEffect(() => {
    loadAllocations();
  }, [loadAllocations]);

  // Load available inventory items for allotment modal
  const loadInventoryItems = async () => {
    setLoadingInv(true);
    try {
      const res = await api.get("/inventory/lookup");
      setInventoryList(res.data?.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingInv(false);
    }
  };

  // Load client's procurement orders
  const loadClientOrders = useCallback(async () => {
    if (!clientId) return;
    setLoadingOrders(true);
    try {
      const res = await api.get("/orders", { params: { client: clientId, orderType: "Procurement", limit: 50 } });
      setClientOrders(res.data?.data?.orders || []);
    } catch (err) {
      console.error(err);
      setClientOrders([]);
    } finally {
      setLoadingOrders(false);
    }
  }, [clientId]);

  const openAllotModal = () => {
    setAllotItemsList([]);
    setPickerItemId("");
    setPickerSearch("");
    setPickerQty("1");
    setPickerRate("");
    setPickerNotes("");
    setPickerError("");
    setAllotStep("form");
    setAllotSuccessData(null);
    setSelectedOrderId("new");
    setAllotNotes("");
    setAllotError("");
    setShowAllotModal(true);
    loadInventoryItems();
    loadClientOrders();
  };

  const pickerItem = useMemo(() => {
    return inventoryList.find((i) => i._id === pickerItemId) || null;
  }, [inventoryList, pickerItemId]);

  // Keep rate in sync when picker item changes
  useEffect(() => {
    if (pickerItem) {
      setPickerRate(pickerItem.rate != null ? String(pickerItem.rate) : "");
      if (!pickerQty || Number(pickerQty) <= 0) {
        setPickerQty("1");
      }
    } else {
      setPickerRate("");
    }
  }, [pickerItem]);

  const filteredInventory = useMemo(() => {
    if (!pickerSearch.trim()) return inventoryList;
    const q = pickerSearch.toLowerCase().trim();
    return inventoryList.filter((i) => {
      const name = (i.name || "").toLowerCase();
      const size = (i.size || "").toLowerCase();
      const sku = (i.sku || "").toLowerCase();
      const cat = (i.category || "").toLowerCase();
      return name.includes(q) || size.includes(q) || sku.includes(q) || cat.includes(q);
    });
  }, [inventoryList, pickerSearch]);

  const handleAddItemToList = (e) => {
    if (e) e.preventDefault();
    setPickerError("");

    if (!pickerItemId || !pickerItem) {
      setPickerError("Please select a material / product from the dropdown.");
      return;
    }
    const q = Number(pickerQty);
    if (!q || q <= 0) {
      setPickerError("Quantity to allot must be greater than zero.");
      return;
    }
    if (q > pickerItem.quantity) {
      setPickerError(`Cannot allot ${q}. Only ${pickerItem.quantity} ${pickerItem.unit || "units"} available in store stock.`);
      return;
    }
    const r = Number(pickerRate);
    if (r < 0) {
      setPickerError("Unit rate cannot be negative.");
      return;
    }

    const existingIdx = allotItemsList.findIndex((i) => i.itemId === pickerItem._id);
    if (existingIdx >= 0) {
      const curQty = Number(allotItemsList[existingIdx].quantity) || 0;
      const combinedQty = curQty + q;
      if (combinedQty > pickerItem.quantity) {
        setPickerError(
          `This item is already in list with ${curQty} units. Adding ${q} more exceeds store stock (${pickerItem.quantity}).`
        );
        return;
      }
      setAllotItemsList((prev) =>
        prev.map((it, idx) =>
          idx === existingIdx
            ? {
                ...it,
                quantity: combinedQty,
                rate: r > 0 ? r : it.rate,
                notes: pickerNotes.trim() ? (it.notes ? `${it.notes}; ${pickerNotes.trim()}` : pickerNotes.trim()) : it.notes,
              }
            : it
        )
      );
    } else {
      setAllotItemsList((prev) => [
        ...prev,
        {
          itemId: pickerItem._id,
          name: pickerItem.name,
          size: pickerItem.size || "",
          unit: pickerItem.unit || "Nos",
          vendor: pickerItem.vendor || "",
          maxStock: Number(pickerItem.quantity) || 0,
          quantity: q,
          rate: r,
          notes: pickerNotes.trim(),
        },
      ]);
    }

    // Reset picker inputs
    setPickerItemId("");
    setPickerSearch("");
    setPickerQty("1");
    setPickerRate("");
    setPickerNotes("");
    setPickerError("");
  };

  const updateAllotItemQty = (index, val) => {
    setAllotItemsList((prev) =>
      prev.map((it, idx) => (idx === index ? { ...it, quantity: val } : it))
    );
  };

  const updateAllotItemRate = (index, val) => {
    setAllotItemsList((prev) =>
      prev.map((it, idx) => (idx === index ? { ...it, rate: val } : it))
    );
  };

  const updateAllotItemNotes = (index, val) => {
    setAllotItemsList((prev) =>
      prev.map((it, idx) => (idx === index ? { ...it, notes: val } : it))
    );
  };

  const removeAllotItem = (index) => {
    setAllotItemsList((prev) => prev.filter((_, idx) => idx !== index));
  };

  const { allotGrandTotal, allotTotalUnits, allotHasInvalidItems } = useMemo(() => {
    let total = 0;
    let units = 0;
    let invalid = false;

    allotItemsList.forEach((it) => {
      const q = Number(it.quantity) || 0;
      const r = Number(it.rate) || 0;
      if (q <= 0 || q > it.maxStock || r < 0) {
        invalid = true;
      }
      total += q * r;
      units += q;
    });

    return {
      allotGrandTotal: Math.round(total * 100) / 100,
      allotTotalUnits: Math.round(units * 100) / 100,
      allotHasInvalidItems: invalid,
    };
  }, [allotItemsList]);

  const handleAllotSubmit = async (e) => {
    if (e) e.preventDefault();
    setAllotError("");

    if (allotItemsList.length === 0) {
      setAllotError("Please add at least one material / hardware item to the allotment list.");
      return;
    }
    if (allotHasInvalidItems) {
      setAllotError("Please review item quantities and rates. Quantities must be > 0 and cannot exceed store stock.");
      return;
    }

    setSubmittingAllot(true);
    try {
      const payload = {
        clientId,
        projectId: projectId || undefined,
        orderId: selectedOrderId,
        notes: allotNotes.trim() || undefined,
        items: allotItemsList.map((it) => ({
          itemId: it.itemId,
          quantity: Number(it.quantity) || 0,
          rate: Number(it.rate) || 0,
          notes: it.notes ? it.notes.trim() : undefined,
        })),
      };

      const res = await api.post("/inventory/bulk-issue", payload);
      const data = res.data?.data || {};

      setAllotSuccessData(data);
      setAllotStep("further-steps");
      loadAllocations();
    } catch (err) {
      setAllotError(err.response?.data?.message || "Failed to allot material.");
    } finally {
      setSubmittingAllot(false);
    }
  };

  const openReturnModal = (tx) => {
    setSelectedTx(tx);
    setReturnQty(String(tx.quantity || ""));
    setReturnNotes("");
    setReturnError("");
    setShowReturnModal(true);
  };

  const handleReturnSubmit = async (e) => {
    e.preventDefault();
    setReturnError("");

    if (!selectedTx) return;

    const qty = Number(returnQty);
    if (!qty || qty <= 0) {
      setReturnError("Please enter a valid quantity greater than zero.");
      return;
    }

    if (qty > selectedTx.quantity) {
      setReturnError(`Return quantity cannot exceed previously allotted quantity (${selectedTx.quantity}).`);
      return;
    }

    setSubmittingReturn(true);
    try {
      const payload = {
        quantity: qty,
        clientId,
        projectId: projectId || undefined,
        notes: returnNotes.trim() || `Returned unused from site (ref: ${selectedTx.itemName})`,
      };

      const res = await api.post(`/inventory/${selectedTx.itemId}/return`, payload);
      setSuccessMsg(res.data?.message || "Material returned to inventory successfully.");
      setTimeout(() => setSuccessMsg(""), 5000);
      setShowReturnModal(false);
      setSelectedTx(null);
      loadAllocations();
    } catch (err) {
      setReturnError(err.response?.data?.message || "Failed to return material.");
    } finally {
      setSubmittingReturn(false);
    }
  };

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto", paddingBottom: 40 }}>
      {/* ── Notification Banner ── */}
      {successMsg && (
        <div
          style={{
            background: "#ECFDF5",
            border: "1px solid #A7F3D0",
            color: "#065F46",
            padding: "12px 16px",
            borderRadius: 8,
            marginBottom: 16,
            fontSize: 13,
            fontWeight: 500,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span>✓ {successMsg}</span>
          <button
            onClick={() => setSuccessMsg("")}
            style={{ background: "none", border: "none", cursor: "pointer", color: "#065F46", fontWeight: 700 }}
          >
            ×
          </button>
        </div>
      )}

      {error && (
        <div
          style={{
            background: "#FEF2F2",
            border: "1px solid #FECACA",
            color: "#991B1B",
            padding: "12px 16px",
            borderRadius: 8,
            marginBottom: 16,
            fontSize: 13,
          }}
        >
          {error}
        </div>
      )}

      {/* ── KPI Summary Cards ── */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 16,
          marginBottom: 20,
        }}
      >
        <div
          style={{
            background: "#fff",
            border: "1px solid #E5E7EB",
            borderRadius: 12,
            padding: "16px 20px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
          }}
        >
          <div style={{ fontSize: 11, fontWeight: 600, color: "#6B7280", textTransform: "uppercase" }}>
            Total Allotted Material Value
          </div>
          <div style={{ fontSize: 22, fontWeight: 700, color: "#976793", marginTop: 4 }}>
            ₹ {formatInr(summary.totalAllottedAmount)}
          </div>
          <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 2 }}>
            Cost of materials & hardware issued to this project
          </div>
        </div>

        <div
          style={{
            background: "#fff",
            border: "1px solid #E5E7EB",
            borderRadius: 12,
            padding: "16px 20px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
          }}
        >
          <div style={{ fontSize: 11, fontWeight: 600, color: "#6B7280", textTransform: "uppercase" }}>
            Net Units Allotted
          </div>
          <div style={{ fontSize: 22, fontWeight: 700, color: "#1F2937", marginTop: 4 }}>
            {summary.netQuantity.toLocaleString("en-IN")} units
          </div>
          <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 2 }}>
            Total quantity currently assigned on-site
          </div>
        </div>

        <div
          style={{
            background: "#fff",
            border: "1px solid #E5E7EB",
            borderRadius: 12,
            padding: "16px 20px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
          }}
        >
          <div style={{ fontSize: 11, fontWeight: 600, color: "#6B7280", textTransform: "uppercase" }}>
            Total Stock Movements
          </div>
          <div style={{ fontSize: 22, fontWeight: 700, color: "#1F2937", marginTop: 4 }}>
            {summary.totalEntries} entries
          </div>
          <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 2 }}>
            Audit log of issuances and returns
          </div>
        </div>
      </div>

      {/* ── Table Card ── */}
      <div
        style={{
          background: "#fff",
          border: "1px solid #E5E7EB",
          borderRadius: 12,
          overflow: "hidden",
          boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
        }}
      >
        <div
          style={{
            padding: "16px 20px",
            borderBottom: "1px solid #E5E7EB",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 12,
          }}
        >
          <div>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: "#1F2937", margin: 0 }}>
              Allotted Materials & Hardware
            </h3>
            <p style={{ fontSize: 12, color: "#6B7280", margin: "2px 0 0 0" }}>
              Items allotted here are automatically deducted from the warehouse inventory in real-time.
            </p>
          </div>

          <button
            onClick={openAllotModal}
            type="button"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              background: "#976793",
              color: "#fff",
              border: "none",
              borderRadius: 8,
              padding: "8px 16px",
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
              transition: "background 0.15s",
            }}
          >
            + Allot Material / Hardware
          </button>
        </div>

        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 800 }}>
            <thead>
              <tr style={{ background: "#F9FAFB", borderBottom: "1px solid #E5E7EB", textAlign: "left" }}>
                <th style={{ padding: "10px 14px", fontWeight: 600, color: "#4B5563" }}>Date</th>
                <th style={{ padding: "10px 14px", fontWeight: 600, color: "#4B5563" }}>Product / Hardware</th>
                <th style={{ padding: "10px 14px", fontWeight: 600, color: "#4B5563" }}>Size / Spec</th>
                <th style={{ padding: "10px 14px", fontWeight: 600, color: "#4B5563" }}>Type</th>
                <th style={{ padding: "10px 14px", fontWeight: 600, color: "#4B5563", textAlign: "right" }}>Qty</th>
                <th style={{ padding: "10px 14px", fontWeight: 600, color: "#4B5563", textAlign: "right" }}>Rate (₹)</th>
                <th style={{ padding: "10px 14px", fontWeight: 600, color: "#4B5563", textAlign: "right" }}>Total (₹)</th>
                <th style={{ padding: "10px 14px", fontWeight: 600, color: "#4B5563" }}>Issued By</th>
                <th style={{ padding: "10px 14px", fontWeight: 600, color: "#4B5563" }}>Work Area / Note</th>
                <th style={{ padding: "10px 14px", fontWeight: 600, color: "#4B5563", textAlign: "center" }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={10} style={{ padding: 40, textAlign: "center", color: "#9CA3AF" }}>
                    <div className="im-spinner" />
                    <p style={{ marginTop: 8 }}>Loading allotted inventory...</p>
                  </td>
                </tr>
              ) : allocations.length === 0 ? (
                <tr>
                  <td colSpan={10} style={{ padding: "36px 20px", textAlign: "center", color: "#6B7280" }}>
                    <p style={{ margin: 0, fontWeight: 500 }}>No materials or hardware have been allotted to this client yet.</p>
                    <p style={{ margin: "4px 0 0 0", fontSize: 12, color: "#9CA3AF" }}>
                      Click <strong>"+ Allot Material / Hardware"</strong> to issue items from store stock.
                    </p>
                  </td>
                </tr>
              ) : (
                allocations.map((tx) => {
                  const isIssue = tx.type === "issue_out";
                  const isReturn = tx.type === "return_in";
                  return (
                    <tr
                      key={tx._id}
                      style={{
                        borderBottom: "1px solid #F3F4F6",
                        background: isReturn ? "#FDF2F8" : "transparent",
                      }}
                    >
                      <td style={{ padding: "10px 14px", color: "#6B7280", whiteSpace: "nowrap" }}>
                        {tx.createdAt ? new Date(tx.createdAt).toLocaleDateString("en-IN") : "—"}
                      </td>
                      <td style={{ padding: "10px 14px", fontWeight: 600, color: "#1F2937" }}>
                        {tx.itemName || "Item"}
                      </td>
                      <td style={{ padding: "10px 14px", color: "#4B5563" }}>
                        {tx.itemSize || "—"}
                      </td>
                      <td style={{ padding: "10px 14px" }}>
                        <span
                          style={{
                            display: "inline-block",
                            padding: "2px 8px",
                            borderRadius: 12,
                            fontSize: 11,
                            fontWeight: 600,
                            background: isIssue ? "#EFF6FF" : isReturn ? "#ECFDF5" : "#F3F4F6",
                            color: isIssue ? "#1D4ED8" : isReturn ? "#047857" : "#4B5563",
                          }}
                        >
                          {isIssue ? "Issued Out" : isReturn ? "Returned" : tx.type}
                        </span>
                      </td>
                      <td
                        style={{
                          padding: "10px 14px",
                          textAlign: "right",
                          fontWeight: 600,
                          color: isIssue ? "#1F2937" : "#047857",
                        }}
                      >
                        {isIssue ? "" : "-"}{tx.quantity}
                      </td>
                      <td style={{ padding: "10px 14px", textAlign: "right", color: "#6B7280" }}>
                        ₹ {formatInr(tx.rate)}
                      </td>
                      <td
                        style={{
                          padding: "10px 14px",
                          textAlign: "right",
                          fontWeight: 600,
                          color: isIssue ? "#976793" : "#047857",
                        }}
                      >
                        {isIssue ? "" : "-"}₹ {formatInr(tx.amount)}
                      </td>
                      <td style={{ padding: "10px 14px", color: "#6B7280", whiteSpace: "nowrap" }}>
                        {tx.createdByName || "Staff"}
                      </td>
                      <td style={{ padding: "10px 14px", color: "#4B5563", maxWidth: 220, fontSize: 12 }}>
                        <div>{tx.notes || tx.reason || "—"}</div>
                        {tx.orderCode && (
                          <div style={{ marginTop: 3 }}>
                            <span
                              style={{
                                display: "inline-block",
                                padding: "2px 7px",
                                borderRadius: 4,
                                fontSize: 10,
                                fontWeight: 600,
                                background: "#F5F3FF",
                                color: "#6D28D9",
                                border: "1px solid #DDD6FE",
                              }}
                            >
                              PO: {tx.orderCode}
                            </span>
                          </div>
                        )}
                      </td>
                      <td style={{ padding: "10px 14px", textAlign: "center" }}>
                        {isIssue && (
                          <button
                            type="button"
                            onClick={() => openReturnModal(tx)}
                            title="Return unused material to inventory"
                            style={{
                              padding: "4px 10px",
                              border: "1px solid #D1D5DB",
                              borderRadius: 6,
                              background: "#fff",
                              color: "#374151",
                              fontSize: 11,
                              cursor: "pointer",
                              whiteSpace: "nowrap",
                            }}
                          >
                            ↩ Return
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Modal: Allot Hardware / Material (Multi-Item & Further Steps) ── */}
      {showAllotModal && (
        <div className="im-overlay" onClick={() => setShowAllotModal(false)}>
          <div
            className="im-modal"
            style={{ maxWidth: 740, width: "95vw", maxHeight: "90vh", display: "flex", flexDirection: "column" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="im-modal-header" style={{ padding: "16px 20px", borderBottom: "1px solid #E5E7EB" }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 17, color: "#1F2937" }}>
                  {allotStep === "further-steps" ? "🎉 Material Allotment Confirmed!" : "Allot Material / Hardware to Client"}
                </h3>
                <p style={{ margin: "3px 0 0 0", fontSize: 12, color: "#6B7280" }}>
                  {allotStep === "further-steps"
                    ? "Items successfully deducted from warehouse stock and recorded to client procurement."
                    : "Choose multiple items to build an allotment list, auto-deduct stock, and generate purchase records."}
                </p>
              </div>
              <button
                className="im-icon-btn"
                onClick={() => setShowAllotModal(false)}
                type="button"
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            <div className="im-modal-body" style={{ padding: "18px 20px", overflowY: "auto", flex: 1 }}>
              {allotError && (
                <div
                  style={{
                    background: "#FEF2F2",
                    border: "1px solid #FECACA",
                    color: "#991B1B",
                    padding: "10px 14px",
                    borderRadius: 8,
                    marginBottom: 16,
                    fontSize: 12,
                  }}
                >
                  {allotError}
                </div>
              )}

              {allotStep === "form" ? (
                <div>
                  {/* ── Section 1: Item Picker (Choose Item -> Add to List) ── */}
                  <div
                    style={{
                      background: "#FAF7FA",
                      border: "1px solid #EADBEC",
                      borderRadius: 8,
                      padding: "14px",
                      marginBottom: 18,
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: "#2E1A46", textTransform: "uppercase" }}>
                        1. Choose Item to Add to Allotment
                      </div>
                      <span style={{ fontSize: 11, color: "#6B7280" }}>
                        Select an item, enter quantity & rate, then click "+ Add to List"
                      </span>
                    </div>

                    {pickerError && (
                      <div
                        style={{
                          background: "#FEF2F2",
                          border: "1px solid #FECACA",
                          color: "#991B1B",
                          padding: "6px 10px",
                          borderRadius: 6,
                          fontSize: 11,
                          marginBottom: 8,
                        }}
                      >
                        {pickerError}
                      </div>
                    )}

                    {/* Filter / Search Product */}
                    <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                      <div style={{ flex: 1, position: "relative" }}>
                        <input
                          type="text"
                          value={pickerSearch}
                          onChange={(e) => setPickerSearch(e.target.value)}
                          placeholder="Filter items by name, size or category..."
                          style={{
                            width: "100%",
                            padding: "7px 10px",
                            fontSize: 12,
                            borderRadius: 6,
                            border: "1px solid #D1D5DB",
                            background: "#fff",
                            boxSizing: "border-box",
                          }}
                        />
                      </div>
                    </div>

                    {/* Product Dropdown */}
                    {loadingInv ? (
                      <div style={{ fontSize: 12, color: "#6B7280", padding: "8px 0" }}>Loading warehouse inventory...</div>
                    ) : (
                      <select
                        value={pickerItemId}
                        onChange={(e) => setPickerItemId(e.target.value)}
                        style={{
                          width: "100%",
                          padding: "8px 10px",
                          borderRadius: 6,
                          border: "1px solid #D1D5DB",
                          fontSize: 13,
                          background: "#fff",
                          marginBottom: 10,
                        }}
                      >
                        <option value="">-- Choose Product / Hardware ({filteredInventory.length} available) --</option>
                        {filteredInventory.map((i) => (
                          <option key={i._id} value={i._id} disabled={i.quantity <= 0}>
                            {i.name} {i.size ? `(${i.size})` : ""} — Stock: {i.quantity} {i.unit || "Nos"} (₹{formatInr(i.rate)}) {i.quantity <= 0 ? " [OUT OF STOCK]" : ""}
                          </option>
                        ))}
                      </select>
                    )}

                    {/* Selected Picker Item Badge */}
                    {pickerItem && (
                      <div
                        style={{
                          background: pickerItem.quantity > 0 ? "#F0FDF4" : "#FEF2F2",
                          border: `1px solid ${pickerItem.quantity > 0 ? "#BBF7D0" : "#FECACA"}`,
                          borderRadius: 6,
                          padding: "8px 12px",
                          marginBottom: 10,
                          fontSize: 12,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <div>
                          <strong style={{ color: pickerItem.quantity > 0 ? "#166534" : "#991B1B" }}>
                            {pickerItem.name} {pickerItem.size ? `(${pickerItem.size})` : ""}
                          </strong>
                          <div style={{ color: "#4B5563", fontSize: 11, marginTop: 1 }}>
                            Available Store Stock: <strong>{pickerItem.quantity} {pickerItem.unit || "units"}</strong> | Base Rate: ₹{formatInr(pickerItem.rate)}
                          </div>
                        </div>
                        {Number(pickerQty) > 0 && Number(pickerRate) > 0 && (
                          <div style={{ textAlign: "right" }}>
                            <div style={{ fontSize: 10, color: "#6B7280" }}>Item Subtotal</div>
                            <strong style={{ fontSize: 13, color: "#976793" }}>
                              ₹ {formatInr(Number(pickerQty) * Number(pickerRate))}
                            </strong>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Inline Qty, Rate, Note & Add Button */}
                    <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1.2fr 2fr auto", gap: 8, alignItems: "end" }}>
                      <div>
                        <label style={{ fontSize: 11, fontWeight: 600, color: "#4B5563", display: "block", marginBottom: 3 }}>
                          Quantity to Allot <span style={{ color: "#EF4444" }}>*</span>
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0.01"
                          max={pickerItem?.quantity || undefined}
                          value={pickerQty}
                          onChange={(e) => setPickerQty(e.target.value)}
                          placeholder="Qty"
                          style={{
                            width: "100%",
                            padding: "7px 10px",
                            fontSize: 12,
                            borderRadius: 6,
                            border: "1px solid #D1D5DB",
                            boxSizing: "border-box",
                          }}
                        />
                      </div>

                      <div>
                        <label style={{ fontSize: 11, fontWeight: 600, color: "#4B5563", display: "block", marginBottom: 3 }}>
                          Unit Rate (₹) <span style={{ color: "#EF4444" }}>*</span>
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={pickerRate}
                          onChange={(e) => setPickerRate(e.target.value)}
                          placeholder="Rate (₹)"
                          style={{
                            width: "100%",
                            padding: "7px 10px",
                            fontSize: 12,
                            borderRadius: 6,
                            border: "1px solid #D1D5DB",
                            boxSizing: "border-box",
                          }}
                        />
                      </div>

                      <div>
                        <label style={{ fontSize: 11, fontWeight: 600, color: "#4B5563", display: "block", marginBottom: 3 }}>
                          Line Note / Purpose (Optional)
                        </label>
                        <input
                          type="text"
                          value={pickerNotes}
                          onChange={(e) => setPickerNotes(e.target.value)}
                          placeholder="e.g. Master Bedroom sliding wardrobe"
                          style={{
                            width: "100%",
                            padding: "7px 10px",
                            fontSize: 12,
                            borderRadius: 6,
                            border: "1px solid #D1D5DB",
                            boxSizing: "border-box",
                          }}
                        />
                      </div>

                      <div>
                        <button
                          type="button"
                          onClick={handleAddItemToList}
                          disabled={!pickerItemId || !pickerItem || pickerItem.quantity <= 0}
                          style={{
                            padding: "8px 16px",
                            background: pickerItemId && pickerItem && pickerItem.quantity > 0 ? "#976793" : "#D1D5DB",
                            color: "#fff",
                            border: "none",
                            borderRadius: 6,
                            fontSize: 12,
                            fontWeight: 600,
                            cursor: pickerItemId && pickerItem && pickerItem.quantity > 0 ? "pointer" : "not-allowed",
                            whiteSpace: "nowrap",
                            height: 34,
                          }}
                        >
                          + Add Item to List
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* ── Section 2: Allotment Items List ── */}
                  <div style={{ marginBottom: 18 }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: "#374151", textTransform: "uppercase" }}>
                        2. Allotment Items List ({allotItemsList.length} {allotItemsList.length === 1 ? "item" : "items"})
                      </div>
                      {allotItemsList.length > 0 && (
                        <button
                          type="button"
                          onClick={() => setAllotItemsList([])}
                          style={{ background: "none", border: "none", color: "#DC2626", fontSize: 11, cursor: "pointer", padding: 0 }}
                        >
                          Clear All Items
                        </button>
                      )}
                    </div>

                    {allotItemsList.length === 0 ? (
                      <div
                        style={{
                          border: "2px dashed #E5E7EB",
                          borderRadius: 8,
                          padding: "24px 16px",
                          textAlign: "center",
                          color: "#9CA3AF",
                          fontSize: 13,
                        }}
                      >
                        📦 No items added to list yet. Select an item above and click <strong>"+ Add Item to List"</strong>.
                      </div>
                    ) : (
                      <div style={{ border: "1px solid #E5E7EB", borderRadius: 8, overflow: "hidden", background: "#fff" }}>
                        <div style={{ overflowX: "auto" }}>
                          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                            <thead>
                              <tr style={{ background: "#F9FAFB", borderBottom: "1px solid #E5E7EB", textAlign: "left" }}>
                                <th style={{ padding: "8px 10px", width: 30 }}>#</th>
                                <th style={{ padding: "8px 10px" }}>Item & Size</th>
                                <th style={{ padding: "8px 10px", width: 90 }}>In Stock</th>
                                <th style={{ padding: "8px 10px", width: 100 }}>Qty to Allot</th>
                                <th style={{ padding: "8px 10px", width: 110 }}>Rate (₹)</th>
                                <th style={{ padding: "8px 10px", width: 110, textAlign: "right" }}>Total (₹)</th>
                                <th style={{ padding: "8px 10px" }}>Line Note</th>
                                <th style={{ padding: "8px 10px", width: 40, textAlign: "center" }}>✕</th>
                              </tr>
                            </thead>
                            <tbody>
                              {allotItemsList.map((it, idx) => {
                                const q = Number(it.quantity) || 0;
                                const r = Number(it.rate) || 0;
                                const lineTotal = Math.round(q * r * 100) / 100;
                                const isQtyExceeded = q > it.maxStock;
                                const isQtyInvalid = q <= 0;

                                return (
                                  <tr
                                    key={idx}
                                    style={{
                                      borderBottom: idx === allotItemsList.length - 1 ? "none" : "1px solid #F3F4F6",
                                      background: isQtyExceeded || isQtyInvalid ? "#FEF2F2" : "transparent",
                                    }}
                                  >
                                    <td style={{ padding: "8px 10px", color: "#9CA3AF", fontWeight: 600 }}>{idx + 1}</td>
                                    <td style={{ padding: "8px 10px" }}>
                                      <div style={{ fontWeight: 600, color: "#1F2937" }}>{it.name}</div>
                                      {it.size && <div style={{ fontSize: 11, color: "#6B7280" }}>{it.size}</div>}
                                    </td>
                                    <td style={{ padding: "8px 10px" }}>
                                      <span
                                        style={{
                                          display: "inline-block",
                                          padding: "2px 6px",
                                          borderRadius: 4,
                                          fontSize: 10,
                                          fontWeight: 600,
                                          background: it.maxStock > 0 ? "#ECFDF5" : "#FEF2F2",
                                          color: it.maxStock > 0 ? "#047857" : "#B91C1C",
                                        }}
                                      >
                                        {it.maxStock} {it.unit}
                                      </span>
                                    </td>
                                    <td style={{ padding: "8px 10px" }}>
                                      <input
                                        type="number"
                                        step="0.01"
                                        min="0.01"
                                        max={it.maxStock}
                                        value={it.quantity}
                                        onChange={(e) => updateAllotItemQty(idx, e.target.value)}
                                        style={{
                                          width: "100%",
                                          padding: "4px 6px",
                                          fontSize: 12,
                                          borderRadius: 4,
                                          border: isQtyExceeded || isQtyInvalid ? "1px solid #EF4444" : "1px solid #D1D5DB",
                                          boxSizing: "border-box",
                                          fontWeight: 600,
                                          color: isQtyExceeded || isQtyInvalid ? "#DC2626" : "inherit",
                                        }}
                                        title={isQtyExceeded ? `Exceeds stock (${it.maxStock})` : ""}
                                      />
                                    </td>
                                    <td style={{ padding: "8px 10px" }}>
                                      <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={it.rate}
                                        onChange={(e) => updateAllotItemRate(idx, e.target.value)}
                                        style={{
                                          width: "100%",
                                          padding: "4px 6px",
                                          fontSize: 12,
                                          borderRadius: 4,
                                          border: "1px solid #D1D5DB",
                                          boxSizing: "border-box",
                                        }}
                                      />
                                    </td>
                                    <td style={{ padding: "8px 10px", textAlign: "right", fontWeight: 700, color: "#976793" }}>
                                      ₹ {formatInr(lineTotal)}
                                    </td>
                                    <td style={{ padding: "8px 10px" }}>
                                      <input
                                        type="text"
                                        value={it.notes || ""}
                                        onChange={(e) => updateAllotItemNotes(idx, e.target.value)}
                                        placeholder="Note"
                                        style={{
                                          width: "100%",
                                          padding: "4px 6px",
                                          fontSize: 11,
                                          borderRadius: 4,
                                          border: "1px solid #E5E7EB",
                                          boxSizing: "border-box",
                                        }}
                                      />
                                    </td>
                                    <td style={{ padding: "8px 10px", textAlign: "center" }}>
                                      <button
                                        type="button"
                                        onClick={() => removeAllotItem(idx)}
                                        style={{
                                          background: "none",
                                          border: "none",
                                          color: "#EF4444",
                                          cursor: "pointer",
                                          fontSize: 14,
                                          padding: 0,
                                        }}
                                        title="Remove item"
                                      >
                                        ✕
                                      </button>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>

                        {/* Summary Bar */}
                        <div
                          style={{
                            padding: "10px 14px",
                            background: "#F5F3FF",
                            borderTop: "1px solid #DDD6FE",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <div style={{ fontSize: 12, color: "#4B5563" }}>
                            Total Selected: <strong>{allotItemsList.length} items</strong> ({allotTotalUnits} units total)
                          </div>
                          <div style={{ textAlign: "right" }}>
                            <span style={{ fontSize: 11, color: "#6B7280", marginRight: 8 }}>Grand Total Value:</span>
                            <strong style={{ fontSize: 16, color: "#7C3AED" }}>₹ {formatInr(allotGrandTotal)}</strong>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* ── Order Destination & Notes ── */}
                  <div className="im-field" style={{ marginBottom: 14 }}>
                    <label style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span>Purchase Expense Order Destination <span style={{ color: "#EF4444" }}>*</span></span>
                      {loadingOrders && <span style={{ fontSize: 10, color: "#6B7280" }}>Checking client orders...</span>}
                    </label>
                    <select
                      value={selectedOrderId}
                      onChange={(e) => setSelectedOrderId(e.target.value)}
                      style={{
                        width: "100%",
                        padding: "8px 10px",
                        borderRadius: 6,
                        border: "1px solid #D1D5DB",
                        fontSize: 12,
                      }}
                    >
                      <option value="new">
                        ➕ Create New Purchase Expense Order (Store Allotment — {allotItemsList.length} items, ₹{formatInr(allotGrandTotal)})
                      </option>
                      {clientOrders.map((ord) => (
                        <option key={ord._id} value={ord._id}>
                          Add to: {ord.orderCode || "Order"} — {ord.title || "Procurement"} (Current Total: ₹{formatInr(ord.total)}) [{ord.status}]
                        </option>
                      ))}
                    </select>
                    <div style={{ fontSize: 11, color: "#6B7280", marginTop: 4 }}>
                      {selectedOrderId === "new"
                        ? `A new approved Purchase Order will be created for this client for all ${allotItemsList.length} item(s) totalling ₹${formatInr(allotGrandTotal)}.`
                        : `These ${allotItemsList.length} items will be appended to the selected purchase order.`}
                    </div>
                  </div>

                  {/* Notes / Work Area */}
                  <div className="im-field" style={{ marginBottom: 4 }}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>
                      Work Area / Purpose / Site Notes
                    </label>
                    <textarea
                      rows={2}
                      value={allotNotes}
                      onChange={(e) => setAllotNotes(e.target.value)}
                      placeholder="e.g. Master Bedroom sliding wardrobe, Kitchen cabinet hinges, etc."
                      style={{
                        width: "100%",
                        padding: "8px 10px",
                        borderRadius: 6,
                        border: "1px solid #D1D5DB",
                        fontSize: 12,
                        boxSizing: "border-box",
                      }}
                    />
                  </div>
                </div>
              ) : (
                /* ── Further Steps View ── */
                <div>
                  <div
                    style={{
                      background: "#F0FDF4",
                      border: "1px solid #BBF7D0",
                      borderRadius: 10,
                      padding: "16px 20px",
                      marginBottom: 16,
                      display: "flex",
                      alignItems: "center",
                      gap: 14,
                    }}
                  >
                    <div
                      style={{
                        width: 44,
                        height: 44,
                        borderRadius: "50%",
                        background: "#DCFCE7",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 22,
                        color: "#16A34A",
                        flexShrink: 0,
                      }}
                    >
                      ✓
                    </div>
                    <div>
                      <h4 style={{ margin: 0, fontSize: 16, color: "#166534", fontWeight: 700 }}>
                        Material Allotment Confirmed Successfully!
                      </h4>
                      <p style={{ margin: "2px 0 0 0", fontSize: 12, color: "#374151" }}>
                        Stock has been auto-deducted from warehouse inventory and recorded into client procurement.
                      </p>
                    </div>
                  </div>

                  {/* KPI Details Cards */}
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10, marginBottom: 16 }}>
                    <div style={{ background: "#F9FAFB", border: "1px solid #E5E7EB", borderRadius: 8, padding: "10px 14px" }}>
                      <div style={{ fontSize: 11, color: "#6B7280", textTransform: "uppercase", fontWeight: 600 }}>Allotted Items</div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: "#1F2937", marginTop: 2 }}>
                        {allotSuccessData?.itemsCount || allotItemsList.length} products
                      </div>
                      <div style={{ fontSize: 11, color: "#059669" }}>{allotTotalUnits} units deducted</div>
                    </div>

                    <div style={{ background: "#F9FAFB", border: "1px solid #E5E7EB", borderRadius: 8, padding: "10px 14px" }}>
                      <div style={{ fontSize: 11, color: "#6B7280", textTransform: "uppercase", fontWeight: 600 }}>Total Value</div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: "#7C3AED", marginTop: 2 }}>
                        ₹ {formatInr(allotSuccessData?.totalAmount || allotGrandTotal)}
                      </div>
                      <div style={{ fontSize: 11, color: "#6B7280" }}>Procurement charge</div>
                    </div>

                    <div style={{ background: "#F9FAFB", border: "1px solid #E5E7EB", borderRadius: 8, padding: "10px 14px" }}>
                      <div style={{ fontSize: 11, color: "#6B7280", textTransform: "uppercase", fontWeight: 600 }}>Purchase Order</div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "#1F2937", marginTop: 2 }}>
                        {allotSuccessData?.order?.orderCode ? `PO: ${allotSuccessData.order.orderCode}` : "Approved"}
                      </div>
                      <div style={{ fontSize: 11, color: "#15803D" }}>Status: Approved</div>
                    </div>
                  </div>

                  {/* Items Allotted Table Preview */}
                  <div style={{ border: "1px solid #E5E7EB", borderRadius: 8, overflow: "hidden", marginBottom: 18 }}>
                    <div style={{ padding: "8px 12px", background: "#F9FAFB", fontSize: 11, fontWeight: 700, color: "#4B5563" }}>
                      DEDUCTED ITEMS BREAKDOWN
                    </div>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                      <tbody>
                        {allotItemsList.map((it, idx) => (
                          <tr key={idx} style={{ borderBottom: idx === allotItemsList.length - 1 ? "none" : "1px solid #F3F4F6" }}>
                            <td style={{ padding: "8px 12px", fontWeight: 600, color: "#1F2937" }}>
                              {it.name} {it.size ? `(${it.size})` : ""}
                            </td>
                            <td style={{ padding: "8px 12px", color: "#059669", fontWeight: 600, textAlign: "right" }}>
                              {it.quantity} {it.unit}
                            </td>
                            <td style={{ padding: "8px 12px", color: "#6B7280", textAlign: "right" }}>
                              @ ₹{formatInr(it.rate)}
                            </td>
                            <td style={{ padding: "8px 12px", fontWeight: 700, color: "#976793", textAlign: "right" }}>
                              ₹ {formatInr(Number(it.quantity) * Number(it.rate))}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Further Steps Actions Card */}
                  <div
                    style={{
                      background: "#FAF7FA",
                      border: "1px solid #E5D5E7",
                      borderRadius: 10,
                      padding: "16px 18px",
                    }}
                  >
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#2E1A46", marginBottom: 6 }}>
                      🚀 Recommended Further Steps
                    </div>
                    <p style={{ margin: "0 0 12px 0", fontSize: 12, color: "#6B7280" }}>
                      Choose where to proceed next with this client's inventory:
                    </p>

                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                      <button
                        type="button"
                        onClick={() => {
                          setAllotItemsList([]);
                          setAllotStep("form");
                          setAllotSuccessData(null);
                        }}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 8,
                          padding: "10px 14px",
                          background: "#fff",
                          color: "#976793",
                          border: "1px solid #976793",
                          borderRadius: 8,
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: "pointer",
                          textAlign: "center",
                        }}
                      >
                        📦 Allot More Items to this Client
                      </button>

                      <button
                        type="button"
                        onClick={() => {
                          setShowAllotModal(false);
                          loadAllocations();
                        }}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 8,
                          padding: "10px 14px",
                          background: "#976793",
                          color: "#fff",
                          border: "none",
                          borderRadius: 8,
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: "pointer",
                          textAlign: "center",
                        }}
                      >
                        ✓ View Client Allocations (Done)
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer (Form Step only) */}
            {allotStep === "form" && (
              <div className="im-modal-footer" style={{ padding: "14px 20px", borderTop: "1px solid #E5E7EB" }}>
                <button
                  type="button"
                  className="im-btn-ghost"
                  onClick={() => setShowAllotModal(false)}
                  disabled={submittingAllot}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleAllotSubmit}
                  className="im-btn-submit"
                  disabled={submittingAllot || allotItemsList.length === 0 || allotHasInvalidItems}
                  style={{
                    background: submittingAllot || allotItemsList.length === 0 || allotHasInvalidItems ? "#D1D5DB" : "#976793",
                    cursor: submittingAllot || allotItemsList.length === 0 || allotHasInvalidItems ? "not-allowed" : "pointer",
                  }}
                >
                  {submittingAllot
                    ? "Deducting Stock..."
                    : `Confirm Allotment (${allotItemsList.length} ${allotItemsList.length === 1 ? "Item" : "Items"})`}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Modal: Return Unused Material ── */}
      {showReturnModal && selectedTx && (
        <div className="im-overlay" onClick={() => setShowReturnModal(false)}>
          <div className="im-modal" style={{ maxWidth: 500 }} onClick={(e) => e.stopPropagation()}>
            <div className="im-modal-header">
              <h3>Return Unused Material to Inventory</h3>
              <button
                className="im-icon-btn"
                onClick={() => setShowReturnModal(false)}
                type="button"
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            <div className="im-modal-body">
              {returnError && (
                <div
                  style={{
                    background: "#FEF2F2",
                    border: "1px solid #FECACA",
                    color: "#991B1B",
                    padding: "8px 12px",
                    borderRadius: 6,
                    marginBottom: 14,
                    fontSize: 12,
                  }}
                >
                  {returnError}
                </div>
              )}

              <div
                style={{
                  background: "#F9FAFB",
                  border: "1px solid #E5E7EB",
                  borderRadius: 8,
                  padding: "10px 14px",
                  marginBottom: 14,
                  fontSize: 12,
                }}
              >
                <div>
                  <strong>Product:</strong> {selectedTx.itemName} {selectedTx.itemSize ? `(${selectedTx.itemSize})` : ""}
                </div>
                <div style={{ marginTop: 2, color: "#6B7280" }}>
                  <strong>Previously Allotted:</strong> {selectedTx.quantity} units (₹{formatInr(selectedTx.rate)}/unit)
                </div>
              </div>

              <form id="return-form" onSubmit={handleReturnSubmit}>
                <div className="im-field">
                  <label>
                    Quantity Returned <span className="im-req">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    max={selectedTx.quantity}
                    value={returnQty}
                    onChange={(e) => setReturnQty(e.target.value)}
                    required
                  />
                  <span style={{ fontSize: 11, color: "#6B7280", marginTop: 4, display: "block" }}>
                    Returned quantity will be added back into warehouse available stock.
                  </span>
                </div>

                <div className="im-field">
                  <label>Return Reason / Notes</label>
                  <textarea
                    rows={2}
                    value={returnNotes}
                    onChange={(e) => setReturnNotes(e.target.value)}
                    placeholder="e.g. Excess hinges returned from site in original packaging."
                  />
                </div>
              </form>
            </div>

            <div className="im-modal-footer">
              <button
                type="button"
                className="im-btn-ghost"
                onClick={() => setShowReturnModal(false)}
                disabled={submittingReturn}
              >
                Cancel
              </button>
              <button
                type="submit"
                form="return-form"
                className="im-btn-submit"
                disabled={submittingReturn}
              >
                {submittingReturn ? "Processing Return..." : "Confirm Return (+ Stock)"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
